import { without } from 'es-toolkit';
import { Filter } from '@devexpress/dx-react-grid';

import { Bundle } from '@paragon/contract-library';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import {
  getBundleGuidFromRowId,
  getBundleRowId,
  getScheduleGroupGuidFromRowId,
  getScheduleGroupRowId,
  GridRow,
  isBundleRow,
  isGroupedProductionComponentInstanceRow,
} from '../../selectors/scheduleGroupGrid';

export function getDescendantRowsGetter(
  rowsKeyedByRowId: { [rowId: string]: GridRow },
  bundlesByScheduleGroupGuid: { [scheduleGroupGuid: string]: readonly Bundle[] },
) {
  return function getDescendantRows(rowId: string) {
    const row = rowsKeyedByRowId[rowId];
    const { type } = row;
    switch (type) {
      case 'scheduleGroup': {
        const scheduleGroupGuid = getScheduleGroupGuidFromRowId(rowId);
        const bundles = bundlesByScheduleGroupGuid[scheduleGroupGuid] ?? [];
        const bundleGuids = bundles.map((bundle) => bundle.guid);
        const bundleRows = bundleGuids
          .map((bundleGuid) => rowsKeyedByRowId[getBundleRowId(bundleGuid)])
          .filter(notNullish);
        const groupedProductionComponentInstanceRows = Object.values(rowsKeyedByRowId).filter(
          (row) => row.type === 'groupedProductionComponentInstance' && bundleGuids.includes(row.bundleGuid),
        );
        return bundleRows.concat(groupedProductionComponentInstanceRows);
      }
      case 'bundle': {
        const bundleGuid = getBundleGuidFromRowId(rowId);
        return Object.values(rowsKeyedByRowId).filter(
          (row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === bundleGuid,
        );
      }
      case 'groupedProductionComponentInstance':
        return [];
      default:
        assertExhaustiveSwitch(type);
    }
  };
}

export function getAncestorRowIds(rowId: string, rowsKeyedByRowId: { [rowId: string]: GridRow }): string[] {
  const row = rowsKeyedByRowId[rowId];
  if (row === undefined) throw new Error('Row not found');
  const { type } = row;
  switch (type) {
    case 'scheduleGroup':
      return [];
    case 'bundle':
      return [getScheduleGroupRowId(row.scheduleGroupGuid)];
    case 'groupedProductionComponentInstance': {
      const bundleRowId = getBundleRowId(row.bundleGuid);
      const bundleRow = rowsKeyedByRowId[bundleRowId];
      if (bundleRow === undefined || bundleRow.type !== 'bundle') throw new Error('Row not found or is wrong type');
      return [bundleRowId, getScheduleGroupRowId(bundleRow.scheduleGroupGuid)];
    }
    default:
      assertExhaustiveSwitch(type);
  }
}

/** This function exists to determine ancestor rows that should be de/selected. They should be de/selected if all
 * siblings are selected. */
export function getAffectedAncestorRowIdsGetter(
  rowsKeyedByRowId: { [rowId: string]: GridRow },
  bundlesByScheduleGroupGuid: { [scheduleGroupGuid: string]: readonly Bundle[] },
  checkedRows: readonly string[],
) {
  return function getAffectedAncestorRowIds(rowId: string): string[] {
    function getAffectedAncestorsForBundleRowOrEmptyArray(bundleRowId: string) {
      const bundleRow = rowsKeyedByRowId[bundleRowId];
      if (bundleRow === undefined || bundleRow.type !== 'bundle') throw new Error('Row not found or is wrong type');
      const siblingRowIds = without(
        bundlesByScheduleGroupGuid[bundleRow.scheduleGroupGuid].map((bundle) => bundle.guid),
        bundleRow.guid,
      ).map((bundleGuid) => getBundleRowId(bundleGuid));
      if (siblingRowIds.every((siblingRowId) => checkedRows.includes(siblingRowId))) {
        return [getScheduleGroupRowId(bundleRow.scheduleGroupGuid)];
      }
      return [];
    }

    if (isBundleRow(rowId)) {
      return getAffectedAncestorsForBundleRowOrEmptyArray(rowId);
    }

    if (isGroupedProductionComponentInstanceRow(rowId)) {
      const groupedProductionComponentInstanceRow = rowsKeyedByRowId[rowId];
      if (
        groupedProductionComponentInstanceRow === undefined ||
        groupedProductionComponentInstanceRow.type !== 'groupedProductionComponentInstance'
      )
        throw new Error('Row not found or is wrong type');
      const { bundleGuid } = groupedProductionComponentInstanceRow;
      const siblingRowIds = Object.values(rowsKeyedByRowId)
        .filter(
          (row) =>
            row.type === 'groupedProductionComponentInstance' && row.rowId !== rowId && row.bundleGuid === bundleGuid,
        )
        .map((row) => row.rowId);
      if (siblingRowIds.every((siblingRowId) => checkedRows.includes(siblingRowId))) {
        const bundleRowId = getBundleRowId(bundleGuid);
        return [bundleRowId].concat(getAffectedAncestorsForBundleRowOrEmptyArray(bundleRowId));
      }
    }

    return [];
  };
}

export function getFilterForSelfAndHierarchy(
  rowsKeyedByRowId: { [rowId: string]: GridRow },
  bundlesByScheduleGroupGuid: { [scheduleGroupGuid: string]: readonly Bundle[] },
) {
  return function filterForSelfAndHierarchyForRow(filterFunction: (row: GridRow, filter: Filter) => boolean) {
    return function filterForSelfAndHierarchy(value: any, filter: Filter, row: GridRow) {
      const descendantRows = getDescendantRowsGetter(rowsKeyedByRowId, bundlesByScheduleGroupGuid)(row.rowId);
      if (descendantRows.some((descendantRow) => filterFunction(descendantRow, filter))) return true;

      const ancestorRows = getAncestorRowIds(row.rowId, rowsKeyedByRowId);
      if (ancestorRows.some((ancestorRowId) => filterFunction(rowsKeyedByRowId[ancestorRowId], filter))) return true;

      return filterFunction(row, filter);
    };
  };
}
